import {
  useGetAdvancedParticipantQuery,
  useGetAdvancedTodaysRecommendationQuery,
} from './advancedParticipant.generated'
import { joinName, toIso } from './format'

export type ParticipantIdentity = {
  name: string
  preferredName?: string | null
  genderIdentity?: string | null
  email?: string | null
  phone?: string | null
  timezone?: string | null
  language?: string | null
  birthDate?: string | null
  stateOfResidence?: string | null
  /** The *therapy* program's start date, not an Advanced track enrolment. */
  programStartDate?: string | null
  enrollmentState?: string | null
  programId?: string | null
  accessCode?: string
  createdAt?: string | null
  firstTimeLoginAt?: string | null
  hrvDevice?: string | null
}

const accessCodeOf = (details: unknown): string | undefined => {
  const accessCode = (details as { accessCode?: unknown } | null | undefined)?.accessCode
  if (!accessCode) {
    return undefined
  }

  return typeof accessCode === 'string' || typeof accessCode === 'number' ? String(accessCode) : 'unknown'
}

export const useParticipantIdentity = (uid: string): ParticipantIdentity => {
  const { data } = useGetAdvancedParticipantQuery({ variables: { uid } })
  const patient = data?.patient

  const name = joinName(patient?.firstname, patient?.lastname)

  return {
    name: name || uid,
    preferredName: patient?.preferredName,
    genderIdentity: patient?.genderIdentity,
    email: patient?.email,
    phone: patient?.phone,
    timezone: patient?.timezone,
    language: patient?.language,
    birthDate: toIso(patient?.birthDate),
    stateOfResidence: patient?.stateOfResidence,
    programStartDate: toIso(patient?.enrollment.programStartDate),
    enrollmentState: patient?.enrollment.state,
    programId: patient?.program?.id,
    accessCode: accessCodeOf(patient?.details),
    createdAt: toIso(patient?.createdAt),
    firstTimeLoginAt: toIso(patient?.privateUserData?.firstTimeLoginAt),
    hrvDevice: patient?.privateUserData?.variables?.hrvDevice,
  }
}

// The therapy program's recommendations, not Advanced's (ADR-030), in the coach's timezone (ADR-056).
export const useTodaysRecommendation = (uid: string) => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const { data, loading } = useGetAdvancedTodaysRecommendationQuery({ variables: { uid, timezone } })

  return {
    loading,
    tasks: data?.taskRecommendationsForPatient.taskRecommendations ?? [],
  }
}
